import { ref } from "vue";
import type {
  StoreApiError,
  FieldError,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import type { AxiosError } from "axios";

export const useFieldErrorMixin = () => {
  const fieldErrors = ref<FieldError[]>([]);

  async function withFieldErrors<T>(promise: Promise<T>): Promise<T> {
    try {
      fieldErrors.value = [];
      return await promise;
    } catch (err: unknown) {
      const axiosError = err as AxiosError;
      if (axiosError.response && axiosError.response.data) {
        const errorData = axiosError.response.data;
        const apiError = errorData as StoreApiError;
        if (apiError.fieldErrors) {
          fieldErrors.value = apiError.fieldErrors;
        }
      }
      throw err;
    }
  }

  function getIsInvalidClass(vField: VField): string {
    if (!vField) {
      return "";
    }
    if (vField.$error) {
      return "is-invalid";
    }
    // split path on last dot
    const fieldName = vField.$path.split(".").pop() as string;
    for (const fieldError of fieldErrors.value) {
      if (fieldError.field === fieldName) {
        return "is-invalid";
      }
    }
    return "";
  }

  interface VField {
    $error: boolean;
    $path: string;
  }

  interface EachResponseDataField {
    $invalid: boolean;
    $error: boolean;
    [validator: string]: boolean;
  }

  interface EachResponseErrorEntry {
    $property: string;
    $message: string;
    $validator: string;
    [key: string]: unknown;
  }

  interface EachResponse {
    $valid: boolean;
    $data: Record<string, EachResponseDataField>[];
    $errors: Record<string, EachResponseErrorEntry[]>[];
  }

  interface EachValidator {
    $response: EachResponse;
    $invalid: boolean;
  }

  /**
   * Builds a VField-like object for a specific field inside an $each-validated array item.
   * This allows FormField and getIsInvalidClass to work with helpers.forEach validated fields.
   *
   * @param formSubmitted - Whether the form has been submitted. Errors are only shown after submission,
   *                        matching the behavior of regular (non-$each) fields.
   */
  function getEachItemFieldVField(
    vFormItems: { $each: EachValidator; $path: string },
    index: number,
    fieldName: string,
    formSubmitted: boolean = false,
  ): VField & Record<string, { $invalid: boolean }> {
    const response = vFormItems?.$each?.$response;
    if (
      !response ||
      !response.$data[index] ||
      !response.$data[index][fieldName]
    ) {
      // @ts-expect-error - vuelidate issue here for some reason
      return {
        $error: false,
        $path: `${vFormItems?.$path || "items"}.${index}.${fieldName}`,
      };
    }

    const fieldData = response.$data[index][fieldName];

    // Build an object compatible with both getIsInvalidClass (needs $error, $path)
    // and FormField's validation message display (needs [validatorKey].$invalid)
    const result: VField & Record<string, unknown> = {
      $error: formSubmitted && fieldData.$error,
      $path: `${vFormItems.$path}.${index}.${fieldName}`,
    };

    // Add each validator's $invalid status so FormField can display messages
    // Only mark as invalid when form was submitted, so messages don't show while typing
    for (const [key, value] of Object.entries(fieldData)) {
      if (!key.startsWith("$")) {
        // value is boolean: true = valid, false = invalid
        result[key] = { $invalid: formSubmitted && !value };
      }
    }
    // @ts-expect-error - vuelidate issue here for some reason
    return result;
  }

  return {
    fieldErrors,
    withFieldErrors,
    getIsInvalidClass,
    getEachItemFieldVField,
  };
};
